/**
 * Carbon inline select for a table toolbar: "Workload: All" on the field
 * fill, the label in secondary text and the value in primary, a chevron 16px
 * from the edge. Pass the label without its colon. Full height, so it sits
 * flush beside TableSearch with a 1px gap; it shares a row with the icon
 * actions below c-md.
 */
import { useId } from 'react'
import clsx from 'clsx'
import { IconChevronDown } from './icons'
import type { SelectOption } from './Select'

export interface TableSelectProps {
  /** "Workload", rendered as "Workload:" and used as the select's name. */
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  /** Not changeable now (an interval while auto-refresh is off): placeholder text, no hover, the select disabled. */
  disabled?: boolean
  className?: string
}

export function TableSelect({ label, value, options, onChange, disabled = false, className }: TableSelectProps) {
  const id = useId()
  return (
    <div
      className={clsx(
        'relative ml-px flex min-h-12 items-center gap-2 border-b bg-field pl-4 text-body',
        disabled
          ? 'cursor-not-allowed border-transparent text-text-placeholder'
          : 'border-line-strong text-text-secondary transition-colors duration-110 ease-carbon hover:bg-layer-hover',
        'focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-focus max-c-md:ml-0 max-c-md:flex-1',
        className,
      )}
    >
      <label htmlFor={id} className="whitespace-nowrap">
        {label}:
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={clsx(
          'h-[47px] min-w-0 flex-1 appearance-none border-0 bg-transparent pr-11 text-body focus:outline-none focus-visible:outline-none [&>option]:bg-layer-2 [&>option]:text-text-primary',
          disabled ? 'cursor-not-allowed text-text-placeholder' : 'cursor-pointer text-text-primary',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <IconChevronDown
        className={clsx('pointer-events-none absolute right-4 top-1/2 -translate-y-1/2', disabled ? 'text-text-placeholder' : 'text-text-primary')}
      />
    </div>
  )
}

export default TableSelect
